import { Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { campaignOffer } from "../../lib/plans";
import type { PlusCopy } from "./plus-copy";
import TmPlusWordmark from "./TmPlusWordmark";

/**
 * The things every TM PLUS format puts above and below its title, in one place
 * so nine formats cannot each drift a shade.
 *
 * They came out of the copy test: seven formats were hand-repeating the same
 * "wordmark · dot · eyebrow" row with only the colour differing, and the arm
 * that replaces that row with a pill and adds a proof line would have meant
 * writing the change seven times.
 *
 * `surface` is ONE decision covering both the wordmark and the text, because
 * they are always the same decision — see PlusBenefits for the same reasoning.
 * `indigo` is the --plus-header gradient, where the wordmark goes brand yellow
 * and nothing may use --plus-tint-*; `lume` is PlusLumeCard's wash, whose
 * eyebrow is a brand-ramp value and needs styles.css's lume-tip-eyebrow rule to
 * survive a dark screen.
 */
export type PlusSurface = "card" | "indigo" | "lume";

const IDENTITY_TEXT: Record<PlusSurface, string> = {
  card: "[color:var(--plus-tint-text)]",
  indigo: "[color:var(--plus-accent)]",
  lume: "lume-tip-eyebrow text-main-600",
};

const WORDMARK_TEXT: Record<PlusSurface, string> = {
  card: "",
  indigo: "text-[#feda00]",
  lume: "[color:var(--plus-tint-text)]",
};

const TAG_CLASS: Record<PlusSurface, string> = {
  card: "[background-color:var(--plus-check-bg)] [color:var(--plus-tint-text)]",
  indigo: "bg-white/17 text-white",
  lume: "[background-color:var(--plus-check-bg)] [color:var(--plus-tint-text)]",
};

/**
 * The wordmark, and beside it either the situation or nothing.
 *
 * TWO WAYS TO SAY THE SAME THING, and which one appears is the test: `eyebrow`
 * is mono caps after a dot, `tag` is a rounded pill in the body face. The pill
 * exists because the mono caps read as a system label — fine for "LIMITE DIÁRIO"
 * and wrong for a sentence like "Vem depois da sua primeira parada", which is
 * what the friction actually is once it leaves the title.
 *
 * Passing both renders only the tag; a format that wants them both stacked does
 * not exist and should not.
 */
export function PlusIdentity({
  className = "",
  copy,
  surface = "card",
  wordmarkClassName = "h-3",
}: {
  className?: string;
  copy: Pick<PlusCopy, "eyebrow" | "tag">;
  surface?: PlusSurface;
  /** The wordmark's height, which differs by format (h-3, h-3.5, h-4.5) */
  wordmarkClassName?: string;
}) {
  const hasTag = copy.tag !== undefined && copy.tag !== "";
  const hasEyebrow = !hasTag && copy.eyebrow !== undefined && copy.eyebrow !== "";

  return (
    <p
      className={`flex flex-wrap items-center gap-2 font-mono text-[10px]/none font-semibold tracking-[0.08em] uppercase ${IDENTITY_TEXT[surface]} ${className}`}
    >
      <TmPlusWordmark
        className={`w-auto shrink-0 ${wordmarkClassName} ${WORDMARK_TEXT[surface]}`}
      />

      {hasEyebrow && (
        <>
          <span
            aria-hidden="true"
            className="size-[3px] rounded-full bg-current opacity-50"
          />
          {copy.eyebrow}
        </>
      )}

      {hasTag && (
        <span
          className={`inline-block rounded-full px-2.5 py-1 font-sans text-[11px]/[1.3] font-bold tracking-normal normal-case ${TAG_CLASS[surface]}`}
        >
          {copy.tag}
        </span>
      )}
    </p>
  );
}

const PROOF_BOX: Record<PlusSurface, string> = {
  card:
    "[background-color:var(--plus-tint-bg)] [border-color:var(--plus-tint-border)]",
  indigo: "bg-white/13 border-white/22",
  lume:
    "[background-color:var(--plus-tint-bg)] [border-color:var(--plus-tint-border)]",
};

const PROOF_TEXT: Record<PlusSurface, string> = {
  card: "text-ink-soft",
  indigo: "text-white/82",
  lume: "text-ink-soft",
};

const PROOF_ACCENT: Record<PlusSurface, string> = {
  card: "[color:var(--plus-tint-text)]",
  indigo: "[color:var(--plus-accent)]",
  lume: "[color:var(--plus-tint-text)]",
};

/**
 * The ENEM number, boxed, at the foot of the formats with room for it.
 *
 * NOT ON EVERY FORMAT, and the two exceptions are the point. The sidebar row
 * shows the same CTA all day: a figure repeated a thousand times stops being
 * proof and becomes decoration. And the tutor's turn is Lume speaking in the
 * first person mid-explanation — a national average dropped into that breaks
 * his voice. The strips and the ribbon skip it for a duller reason: they have
 * one line and it is already spent.
 *
 * Renders nothing without `proof`, so a format can call it unconditionally.
 */
export function PlusProof({
  className = "",
  copy,
  surface = "card",
}: {
  className?: string;
  copy: Pick<PlusCopy, "proof" | "proofValue">;
  surface?: PlusSurface;
}) {
  if (copy.proof === undefined || copy.proof === "") {
    return null;
  }

  return (
    <p
      className={`flex items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5 ${PROOF_BOX[surface]} ${className}`}
    >
      <Sparkles
        aria-hidden="true"
        className={`size-[15px] shrink-0 ${PROOF_ACCENT[surface]}`}
      />

      <span
        className={`min-w-0 text-[12px]/[1.45] font-semibold text-pretty ${PROOF_TEXT[surface]}`}
      >
        {emphasise(copy.proof, copy.proofValue, (matched) => (
          <strong
            className={`text-[14.5px] font-bold tracking-[-0.01em] whitespace-nowrap ${PROOF_ACCENT[surface]}`}
          >
            {matched}
          </strong>
        ))}
      </span>
    </p>
  );
}

const OFFER_TEXT: Record<PlusSurface, string> = {
  card: "text-ink",
  indigo: "text-white",
  lume: "text-ink",
};

const OFFER_MUTED: Record<PlusSurface, string> = {
  card: "text-ink-soft",
  indigo: "text-white/70",
  lume: "text-ink-soft",
};

const OFFER_PRICE: Record<PlusSurface, string> = {
  card: "rounded-[3px] px-1 [background-color:var(--plus-price-bg)] [color:var(--plus-price-text)]",
  indigo: "[color:var(--plus-accent)]",
  lume: "rounded-[3px] px-1 [background-color:var(--plus-price-bg)] [color:var(--plus-price-text)]",
};

export const OFFER_CHIP =
  "inline-block rounded-full px-2.5 py-1 font-mono text-[10px]/none font-extrabold tracking-[0.06em] whitespace-nowrap uppercase [background-color:var(--plus-accent)] [color:var(--plus-cta-hover)]";

/**
 * The campaign's price, in the three shapes the formats have room for:
 *
 *   block   cards, bands and dialogs — chip, "{offer} por R$ X até o ENEM." and
 *           the struck price with what the month is.
 *   inline  strips and the tutor's turn — chip and sentence, nothing struck.
 *   text    the sidebar row — "N% OFF · Reta final por R$ X", no `offer`.
 *
 * Renders nothing outside the campaign, or when the placement has no `offer`
 * (text excepted). The percentage is said once per placement, on the chip.
 *
 * Without `surface` the text inherits its colour, for grounds that are not one
 * of the three (the sidebar, the chat's dark panel).
 */
export function PlusOffer({
  className = "",
  format,
  offer,
  surface,
}: {
  className?: string;
  format: "block" | "inline" | "text";
  offer?: PlusCopy["offer"];
  surface?: PlusSurface;
}) {
  const campaign = campaignOffer();

  if (campaign === null) {
    return null;
  }

  const price = OFFER_PRICE[surface ?? "card"];

  if (format === "text") {
    return (
      <span className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        <span className={OFFER_CHIP}>{campaign.percentOff}% OFF</span>
        <span>
          Reta final por{" "}
          <strong className={`font-bold whitespace-nowrap ${price}`}>{campaign.price}</strong>
        </span>
      </span>
    );
  }

  if (offer === undefined || offer === "") {
    return null;
  }

  const chip = <span className={OFFER_CHIP}>{campaign.percentOff}% OFF na reta final</span>;
  const sentence = (
    <>
      {offer} por{" "}
      <strong className={`font-extrabold whitespace-nowrap ${price}`}>{campaign.price}</strong> até o ENEM.
    </>
  );

  if (format === "inline") {
    return (
      <p
        className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px]/[1.4] font-semibold ${surface ? OFFER_TEXT[surface] : ""} ${className}`}
      >
        {chip}
        <span>{sentence}</span>
      </p>
    );
  }

  return (
    <div className={className}>
      {chip}
      <p className={`mt-2 text-[14px]/[1.4] font-bold text-pretty ${surface ? OFFER_TEXT[surface] : ""}`}>
        {sentence}
      </p>
      <p className={`mt-1 text-[12px]/[1.45] font-medium ${surface ? OFFER_MUTED[surface] : "opacity-70"}`}>
        De <s>{campaign.was}</s> · 1 mês de acesso · Sem renovação automática
      </p>
    </div>
  );
}

/**
 * Wraps `phrase` where it occurs in `text`, and returns `text` untouched when it
 * does not occur — or when there is no phrase at all.
 *
 * THE PHRASE IS FOUND INSIDE THE STRING rather than passed as a second half of
 * it, so there is only ever one sentence to write and no way for the two to
 * disagree. It is how `mark` has always worked against `title` (PlusMarkCard);
 * `proofValue` against `proof` is the same trick, and this is the one copy of it.
 *
 * First occurrence only. A phrase that appears twice marks the first, which is
 * the right answer for a highlighter and has never come up for a number.
 */
export function emphasise(
  text: string,
  phrase: string | undefined,
  render: (matched: string) => ReactNode
): ReactNode {
  const at = phrase === undefined || phrase === "" ? -1 : text.indexOf(phrase);

  if (at === -1 || phrase === undefined) {
    return text;
  }

  return (
    <>
      {text.slice(0, at)}
      {render(phrase)}
      {text.slice(at + phrase.length)}
    </>
  );
}
